<template>
  <aside class="cs">
    <header class="cs__header">
      <div class="cs__title">
        <h4>{{ $t('chat.title') }}</h4>
        <span
          class="cs__live"
          :class="{ 'cs__live--off': !live }"
          :title="live ? $t('chat.live') : $t('chat.reconnecting')"
        >
          <i /> {{ live ? $t('chat.live') : $t('chat.reconnecting') }}
        </span>
        <b-dropdown
          right
          no-caret
          variant="link"
          class="cs__notif"
          toggle-class="cs__sound p-0"
          menu-class="cs__notif-menu"
          :title="$t('chat.notifications.title')"
        >
          <template #button-content>
            <span
              class="cs__bell"
              :class="{ 'is-off': !sound && !alerts, 'is-partial': sound !== alerts }"
            >
              <feather-icon
                :icon="sound || alerts ? 'BellIcon' : 'BellOffIcon'"
                size="17"
              />
            </span>
          </template>
          <b-dropdown-form form-class="p-0">
            <div class="cs__pop">
              <h6 class="cs__pop-title">
                <feather-icon
                  icon="BellIcon"
                  size="15"
                />
                {{ $t('chat.notifications.title') }}
              </h6>

              <div class="cs__opt">
                <span class="cs__opt-icon"><feather-icon
                  icon="Volume2Icon"
                  size="16"
                /></span>
                <span class="cs__opt-text">
                  <strong>{{ $t('chat.notifications.sound') }}</strong>
                  <small>{{ $t('chat.notifications.soundHint') }}</small>
                </span>
                <b-form-checkbox
                  v-model="sound"
                  switch
                  class="cs__opt-switch"
                  :aria-label="$t('chat.notifications.sound')"
                  @change="saveSound"
                />
              </div>

              <div class="cs__opt">
                <span class="cs__opt-icon"><feather-icon
                  icon="MonitorIcon"
                  size="16"
                /></span>
                <span class="cs__opt-text">
                  <strong>{{ $t('chat.notifications.alerts') }}</strong>
                  <small>{{ $t('chat.notifications.alertsHint') }}</small>
                </span>
                <b-form-checkbox
                  v-model="alerts"
                  switch
                  class="cs__opt-switch"
                  :aria-label="$t('chat.notifications.alerts')"
                  @change="saveAlerts"
                />
              </div>

              <p
                v-if="alerts && permission === 'denied'"
                class="cs__pop-warn"
              >
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="14"
                />
                {{ $t('chat.notifications.blocked') }}
              </p>
            </div>
          </b-dropdown-form>
        </b-dropdown>
      </div>

      <label class="cs__search">
        <feather-icon
          icon="SearchIcon"
          size="16"
        />
        <input
          v-model="term"
          type="search"
          :placeholder="$t('chat.search')"
          :aria-label="$t('chat.search')"
        >
      </label>

      <div
        class="cs__tabs"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          :aria-selected="tab === 'conversations'"
          :class="{ 'is-active': tab === 'conversations' }"
          @click="tab = 'conversations'"
        >
          {{ $t('chat.tabs.conversations') }}
          <span
            v-if="unreadTotal"
            class="cs__count cs__count--alert"
          >{{ unreadTotal }}</span>
        </button>
        <button
          type="button"
          role="tab"
          :aria-selected="tab === 'online'"
          :class="{ 'is-active': tab === 'online' }"
          @click="tab = 'online'"
        >
          {{ $t('chat.tabs.online') }}
          <span class="cs__count">{{ contacts.length }}</span>
        </button>
      </div>
    </header>

    <div class="cs__list">
      <!-- Conversas -->
      <template v-if="tab === 'conversations'">
        <button
          v-for="c in filteredConversations"
          :key="c.id"
          type="button"
          class="cs__item"
          :class="{ 'is-active': c.id === activeId }"
          @click="$emit('select-conversation', c.id)"
        >
          <chat-avatar
            :name="c.peer ? c.peer.name : ''"
            :photo="c.peer ? c.peer.photo_url : null"
            :size="48"
            :status="peerStatus(c)"
          />
          <span class="cs__main">
            <span class="cs__row">
              <strong>{{ c.peer ? c.peer.name : '' }}</strong>
              <small :class="{ 'cs__time--unread': c.unread }">{{ lastTime(c) }}</small>
            </span>
            <span class="cs__row">
              <span class="cs__preview">
                <template v-if="c.last_message">
                  <span
                    v-if="c.last_message.sender_id === me"
                    class="cs__you"
                  >{{ $t('chat.you') }}:</span>
                  <feather-icon
                    v-if="previewIcon(c.last_message)"
                    :icon="previewIcon(c.last_message)"
                    size="13"
                    class="cs__preview-icon"
                  />
                  {{ preview(c.last_message) }}
                </template>
                <em v-else>{{ $t('chat.noMessages') }}</em>
              </span>
              <span
                v-if="c.unread"
                class="cs__badge"
              >{{ c.unread > 99 ? '99+' : c.unread }}</span>
            </span>
          </span>
        </button>

        <p
          v-if="!filteredConversations.length"
          class="cs__empty"
        >
          <feather-icon
            icon="MessageSquareIcon"
            size="34"
          />
          <span>{{ term ? $t('chat.noResults') : $t('chat.noConversations') }}</span>
          <button
            v-if="!term"
            type="button"
            class="cs__link"
            @click="tab = 'online'"
          >{{ $t('chat.startOne') }}</button>
        </p>
      </template>

      <!-- Quem está online agora -->
      <template v-else>
        <section
          v-for="group in contactGroups"
          :key="group.nodeId"
          class="cs__group"
        >
          <h6
            v-if="contactGroups.length > 1"
            class="cs__group-title"
          >
            <feather-icon
              icon="GitBranchIcon"
              size="13"
            />
            {{ group.nodeName }}
            <span>{{ group.users.length }}</span>
          </h6>
          <button
            v-for="u in group.users"
            :key="u.user_id"
            type="button"
            class="cs__item"
            :disabled="starting === u.user_id"
            @click="$emit('select-contact', u.user_id)"
          >
            <chat-avatar
              :name="u.name"
              :photo="u.photo_url"
              :size="48"
              :status="u.status"
            />
            <span class="cs__main">
              <span class="cs__row">
                <strong>{{ u.name }}</strong>
                <b-spinner
                  v-if="starting === u.user_id"
                  small
                />
              </span>
              <span class="cs__row">
                <span class="cs__preview">{{ $t(`chat.status.${u.status}`) }}</span>
              </span>
            </span>
          </button>
        </section>

        <p
          v-if="!filteredContacts.length"
          class="cs__empty"
        >
          <feather-icon
            icon="UsersIcon"
            size="34"
          />
          <span>{{ term ? $t('chat.noResults') : $t('chat.nobodyOnline') }}</span>
        </p>
      </template>
    </div>
  </aside>
</template>

<script>
import { listTimeLabel, messagePreviewKey } from '@/utils/chat'
import {
  soundEnabled, setSoundEnabled, alertsEnabled, setAlertsEnabled, notificationPermission, requestNotificationPermission,
} from '@/utils/chatNotify'
import ChatAvatar from './ChatAvatar.vue'

const PREVIEW_ICONS = { image: 'ImageIcon', audio: 'MicIcon', file: 'PaperclipIcon' }

export default {
  name: 'ChatSidebar',

  components: { ChatAvatar },

  props: {
    activeId: { type: Number, default: null },
    starting: { type: Number, default: null },
  },

  data() {
    return {
      tab: 'conversations', term: '', sound: soundEnabled(), alerts: alertsEnabled(), permission: notificationPermission(),
    }
  },

  computed: {
    chat() {
      return this.$store.state.chat
    },
    me() {
      return this.$store.state.user.id
    },
    live() {
      return this.chat.live
    },
    contacts() {
      return this.chat.contacts
    },
    unreadTotal() {
      return this.$store.getters['chat/unreadTotal']
    },
    normalized() {
      return this.term.trim().toLowerCase()
    },
    filteredConversations() {
      const t = this.normalized

      return this.chat.conversations.filter(c => !t || (c.peer && c.peer.name.toLowerCase().includes(t)))
    },
    // Em um nó pai os contatos vêm de vários nós: agrupa por nó (o nó atual primeiro). Em nó folha, sem cabeçalho.
    contactGroups() {
      const currentId = this.$store.state.selected_node ? this.$store.state.selected_node.id : null
      const byNode = new Map()
      this.filteredContacts.forEach(u => {
        if (!byNode.has(u.node_id)) byNode.set(u.node_id, { nodeId: u.node_id, nodeName: u.node_name || '', users: [] })
        byNode.get(u.node_id).users.push(u)
      })

      return [...byNode.values()].sort((a, b) => (
        (b.nodeId === currentId) - (a.nodeId === currentId) || a.nodeName.localeCompare(b.nodeName)
      ))
    },
    filteredContacts() {
      const t = this.normalized

      return this.contacts.filter(u => !t || u.name.toLowerCase().includes(t) || (u.node_name || '').toLowerCase().includes(t))
    },
  },

  methods: {
    saveSound(on) {
      setSoundEnabled(on)
    },

    saveAlerts(on) {
      setAlertsEnabled(on)
      // Quem ainda não respondeu (ou fechou) o pedido de permissão do navegador pode refazê-lo por aqui.
      if (on) {
        requestNotificationPermission()
        this.permission = notificationPermission()
      }
    },

    peerStatus(conversation) {
      return conversation.peer ? this.$store.getters['chat/presenceOf'](conversation.peer) : null
    },

    lastTime(conversation) {
      return conversation.last_message ? listTimeLabel(conversation.last_message.created_at, this.$i18n.locale, this.$t('chat.yesterday')) : ''
    },

    previewIcon(message) {
      return PREVIEW_ICONS[message.kind] || null
    },

    preview(message) {
      if (message.body) return message.body
      const key = messagePreviewKey(message)

      return key ? this.$t(key) : ''
    },
  },
}
</script>

<style scoped>
.cs {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  border-right: 1px solid var(--chat-border);
  background: var(--chat-surface);
}

.cs__header { padding: 16px 16px 8px; }

.cs__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.cs__title h4 { margin: 0 auto 0 0; color: var(--chat-text); font-weight: 700; }

.cs__live {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #28c76f;
  font-size: 0.72rem;
  font-weight: 600;
}

.cs__live i { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.cs__live--off { color: #ff9f43; }
.cs__live--off i { animation: cs-blink 1s ease-in-out infinite; }

@keyframes cs-blink { 50% { opacity: 0.25; } }

.cs__search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  padding: 0 12px;
  border-radius: 10px;
  background: var(--chat-input);
  color: var(--chat-muted);
}

.cs__search input {
  flex: 1;
  min-width: 0;
  height: 38px;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--chat-text);
  font-size: 0.9rem;
}

.cs__search:focus-within { box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.18); }

.cs__tabs {
  display: flex;
  gap: 4px;
  padding: 3px;
  border-radius: 10px;
  background: var(--chat-input);
}

.cs__tabs button {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--chat-muted);
  font-size: 0.84rem;
  font-weight: 600;
  transition: background 0.15s ease, color 0.15s ease;
}

.cs__tabs button.is-active {
  background: var(--chat-surface);
  box-shadow: 0 1px 3px rgba(34, 41, 47, 0.16);
  color: #7367f0;
}

.cs__count {
  min-width: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: rgba(127, 127, 127, 0.18);
  font-size: 0.7rem;
  line-height: 20px;
}

.cs__count--alert { background: #7367f0; color: #fff; }

.cs__list { flex: 1; padding-top: 4px; overflow-y: auto; }

.cs__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  border: 0;
  background: transparent;
  color: var(--chat-text);
  text-align: left;
  transition: background 0.12s ease;
}

.cs__item:hover { background: rgba(127, 127, 127, 0.08); }
.cs__item.is-active { background: rgba(115, 103, 240, 0.12); }
.cs__item:disabled { opacity: 0.6; }

.cs__main { display: flex; flex: 1; flex-direction: column; gap: 3px; min-width: 0; }

.cs__row { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-width: 0; }
.cs__row strong { overflow: hidden; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.cs__row small { flex: none; color: var(--chat-muted); font-size: 0.72rem; }
.cs__time--unread { color: #7367f0 !important; font-weight: 700; }

.cs__preview {
  overflow: hidden;
  color: var(--chat-muted);
  font-size: 0.83rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cs__preview-icon { margin-right: 2px; vertical-align: -2px; }
.cs__you { margin-right: 3px; }

.cs__badge {
  flex: none;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #7367f0;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
}

.cs__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin: 8vh 24px 0;
  color: var(--chat-muted);
  text-align: center;
}

.cs__link { border: 0; background: none; color: #7367f0; font-weight: 600; }

.cs__notif ::v-deep .cs__sound {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-left: 8px;
  padding: 0;
  border: 0;
  box-shadow: none;
  border-radius: 50%;
  background: transparent;
  color: var(--chat-muted);
  cursor: pointer;
  transition: background 0.15s;
}

.cs__notif ::v-deep .cs__sound:hover { background: var(--chat-input); }

@media (max-width: 767px) {
  .cs__search input { font-size: 16px; }
  .cs__item { padding: 12px 14px; }
}

.cs__group-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 12px 16px 4px;
  color: var(--chat-muted);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.cs__group-title span {
  margin-left: auto;
  padding: 0 7px;
  border-radius: 9px;
  background: var(--chat-input);
  font-size: 0.68rem;
}

.cs__bell { position: relative; display: inline-flex; }
.cs__bell.is-off { opacity: 0.5; }

/* Só uma das duas coisas ligada: ponto no canto do sino. */
.cs__bell.is-partial::after {
  content: '';
  position: absolute;
  top: -1px;
  right: -1px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #7367f0;
  box-shadow: 0 0 0 2px var(--chat-surface);
}

.cs__notif ::v-deep .cs__notif-menu {
  width: 340px;
  max-width: calc(100vw - 24px);
  min-width: 0;
  margin-top: 8px;
  padding: 0;
  border: 1px solid var(--chat-border);
  border-radius: 14px;
  background: var(--chat-surface);
  box-shadow: 0 14px 36px rgba(34, 41, 47, 0.22);
}

.cs__pop { padding: 18px 20px 8px; }

.cs__pop-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  color: var(--chat-text);
  font-size: 0.95rem;
  font-weight: 700;
}

.cs__opt {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 0;
  border-top: 1px solid var(--chat-border);
}

.cs__opt-icon {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
}

.cs__opt-text { display: flex; min-width: 0; flex: 1; flex-direction: column; gap: 1px; }
.cs__opt-text { gap: 3px; }
.cs__opt-text strong { color: var(--chat-text); font-size: 0.9rem; font-weight: 600; }
.cs__opt-text small { color: var(--chat-muted); font-size: 0.76rem; line-height: 1.3; }
.cs__opt-switch { flex: none; margin: 0; padding-left: 2.6rem; }

.cs__pop-warn {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 10px;
  padding: 9px 10px;
  border-radius: 8px;
  background: rgba(234, 84, 85, 0.1);
  color: #ea5455;
  font-size: 0.76rem;
  line-height: 1.3;
}
</style>
